import { createContext, useContext, useEffect, useState } from 'react';
import type { DirectFcfsEntrypoint } from 'src/apis/direct-fcfs/types';
import { useAxios } from 'src/hooks/use-axios';

export type DirectFcfsFlags = {
  data?: DirectFcfsEntrypoint;
  error?: unknown;
};

const DirectFcfsFlagsContext = createContext<DirectFcfsFlags>({});

function useEntrypointRequest() {
  const [error, setError] = useState<unknown>(undefined);

  const entrypoint = useAxios(
    (axios, baseConfig) =>
      axios.request<DirectFcfsEntrypoint>({
        ...baseConfig,
        url: `${PRISMA_CONFIG.directFcfs}/entrypoint`,
        method: 'GET',
        timeout: 5000,
      }),
    {
      neededOnPageLoad: true,
      onError: setError,
    }
  );

  useEffect(() => {
    entrypoint.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  return {
    loaded: Boolean(entrypoint.response || error),
    data: entrypoint.response?.data,
    error,
  };
}

type DirectFcfsFlagsValue = {
  loaded: boolean;
  value: DirectFcfsFlags;
};

export function useDirectFcfsFlagsValue(): DirectFcfsFlagsValue {
  const { loaded, data, error } = useEntrypointRequest();
  return {
    loaded,
    value: {
      data,
      error,
    },
  };
}

export function useDirectFcfsFlags() {
  return useContext(DirectFcfsFlagsContext);
}

export const ApiVersions = {
  /**
   * Version 2: ONC-1288 IAM - Reservation and Booking interfaces
   */
  V2_ONC_1288: 2,
  /**
   * Version 3: ONC-1550 DFCFS - Allows custom runtimes (added customRuntime flag).
   */
  V3_ONC_1550: 3,
  /**
   * Version 4: ONC-1549 - Implement OAuth2 for Booking Webhook
   */
  V4_ONC_1550: 4,
  /**
   * Version 5: ONC-1608 - Add portfolio code / balancing group check
   */
  V5_ONC_1608: 5,
} as const;

export function useSupportsApiVersion() {
  const flags = useDirectFcfsFlags();

  const apiVersion = flags.data?.apiVersion ?? 0;

  return (requiredApiVersion: (typeof ApiVersions)[keyof typeof ApiVersions]) =>
    apiVersion >= requiredApiVersion;
}

export const DirectFcfsFlagsProvider = DirectFcfsFlagsContext.Provider;
